import { take } from '../vfx-budget';
import { spawnQuad } from '../vfx-pool';
import { texNeedle, texPolyFill } from '../vfx-tex2';
import { GC, band, core, fan, gband, scorch, shard } from './geo-kit';
import { FLAT, rnd, yawOf } from './util';
/* ============================================================
   風格 A：副武器／大招／掛載
   ============================================================

   全部由 `geo-kit.ts` 的五筆組成，沒有任何一招引進新的形狀語言。
   招與招之間的差別只有三件事：**量體多大、碎片幾片、地面留不留痕**。

   | 招 | 量體 | 碎片 | 地面 |
   |---|---|---|---|
   | 掛載開火 | 最小 | 1 片 | 無 |
   | 飛彈齊射 | 小 | 3 片 | 一圈淺帶 |
   | 光束落點 | 極小 | 2 片 | 無（每 0.07 秒打一次，要最便宜） |
   | 飛彈爆炸 | 中 | 8 片 | 帶 + 焦痕 |
   | 軌道光柱 | 大 | 10 片 | 兩層帶 + 大焦痕 |
   | 大招發射 | 最大 | 10 片 | 兩層帶 |

   這張表本身就是「打擊感的層級」。第三版的問題是每一招都給滿
   （每一招都有火球＋煙＋塵），所以最重的一擊跟最輕的一擊看起來一樣重。 */

/** 爆炸（飛彈／地雷／自爆）。`r` 是爆炸半徑，特效尺寸一律從它推導 ——
    寫死尺寸的話，Lv1 的小飛彈跟進化後的大範圍看起來一樣大。 */
export function explode(x: number, y: number, z: number, _c: number, r: number, power: number) {
  if (!take(15)) return;
  /* 爆心比爆炸半徑**小**（0.7r）。第一版給 1.15r，一群飛彈同時炸的時候
     那些白色實心六邊形會疊成一團沒有形狀的白 —— 正是第三版被否掉的症狀，
     只是換成白色。擴散帶（空心）疊起來仍然讀得出層次，實心的不行。 */
  core(6, x, y, z, r * 0.7, r * 0.22, 0.075, GC.hot);
  band(x, y, z, 0.24, r * 0.7, r * 3.0, 0.16, GC.acc);
  fan(x, y, z, 0, 3.15, 8, r * 3.2, r * 0.26);
  /* 地面兩層：快的一圈帶（衝擊）＋ 慢的焦痕（殘留）。
     時間差就是「剛剛這裡炸過」的證據 —— 同時消失的話讀成一次閃光。 */
  gband(x, z, 0.18, r * 0.8, r * 3.4 * power, 0.30, GC.acc, 0.8);
  scorch(x, z, r * 2.2, 1.1);
}

/** 發射（飛彈齊射、掛架開架）。**沒有煙** ——
    第三版用一團白煙表達「被推出去」，但煙是柔邊的，在硬邊語言裡是異物。
    改成一片沿射向的厚帶 + 三片往後噴的碎片，讀起來一樣是「推力」。 */
export function launch(x: number, y: number, z: number, ang: number) {
  if (!take(8)) return;
  core(6, x, y, z, 1.2, 0.45, 0.06, GC.hot);
  band(x + Math.sin(ang) * 1.2, y, z + Math.cos(ang) * 1.2, 0.26, 1.4, 4.2, 0.10, GC.acc);
  for (var i = 0; i < 3; i++) shard(x, y, z, ang + Math.PI, 0.9, GC.chip, 11, 0.6);
  gband(x, z, 0.16, 2.0, 7.0, 0.26, GC.acc, 0.5);
}

/** 大招發射的那一拍。**全遊戲最重的一次後座**，所以量體給到最大，
    而且是**兩層不同速度的帶** —— 一層讀成閃光，兩層才讀成衝擊有厚度。 */
export function ultFire(x: number, y: number, z: number, ang: number) {
  if (!take(26)) return;
  core(6, x, y, z, 3.4, 1.0, 0.10, GC.hot);
  band(x + Math.sin(ang) * 1.8, y, z + Math.cos(ang) * 1.8, 0.30, 2.6, 9.5, 0.14, GC.hot);
  band(x + Math.sin(ang) * 1.8, y, z + Math.cos(ang) * 1.8, 0.16, 4.0, 15.0, 0.22, GC.acc, 0.8);
  // 後座：碎片往射向的反側甩，讀成「整台被推退」
  fan(x, y, z, ang + Math.PI, 0.9, 10, 20, 0.85);
  // 腳下兩圈不同速度的地面帶
  gband(x, z, 0.20, 3.0, 17.0, 0.30, GC.hot, 0.85);
  gband(x, z, 0.10, 5.0, 27.0, 0.48, GC.acc, 0.5);
}

/** 光束掃到東西的落點。每 0.07 秒的傷害節拍打一次，所以**必須最便宜** ——
    這裡只花 3 個額度，其餘全部省下來給爆炸。 */
export function beamHit(x: number, y: number, z: number) {
  if (!take(3)) return;
  core(5, x, y, z, 0.75, 0.25, 0.055, GC.hot);
  shard(x, y, z, rnd(0, 6.283), 3.15, GC.chip, 12, 0.7);
  shard(x, y, z, rnd(0, 6.283), 3.15, GC.hot, 10, 0.55);
}

/** 掛載開火：比主砲小一號的核心 + 一片碎片。

    十把掛載共用這一拍（子彈是共用材質，分不出誰是誰）——
    分辨度之後要靠彈體形狀處理，不是靠在這裡加顏色。 */
export function gadgetFire(_id: string, x: number, y: number, z: number, ang: number) {
  if (!take(4)) return;
  core(6, x, y, z, 0.9, 0.3, 0.05, GC.hot);
  band(x, y, z, 0.28, 0.6, 1.8, 0.07, GC.acc);
  shard(x, y, z, ang + Math.PI, 0.8, GC.chip, 9, 0.45);
}

/** 軌道光柱落地。**全遊戲最重的一擊**，量體與焦痕都給最大。 */
export function orbitalHit(x: number, z: number, r: number) {
  if (!take(18)) return;
  core(6, x, 1.4, z, r * 0.5, r * 0.18, 0.10, GC.hot);
  band(x, 1.2, z, 0.22, r * 0.6, r * 2.4, 0.16, GC.acc);
  fan(x, 1.0, z, 0, 3.15, 10, r * 2.4, r * 0.20);
  gband(x, z, 0.20, r * 0.7, r * 3.2, 0.32, GC.hot, 0.9);
  gband(x, z, 0.10, r * 1.2, r * 4.6, 0.55, GC.acc, 0.5);
  scorch(x, z, r * 2.6, 1.4);
}

/** 斬擊。硬邊語言裡近戰不發光 —— 用一片**沿弧拉長的針**當刃光，
    量體交給地上那一道帶。刃光只有 0.09 秒，寬度極窄。 */
export function slashArc(x: number, y: number, z: number, yaw: number, range: number) {
  if (!take(9)) return;
  spawnQuad(texNeedle(), x + Math.sin(yaw) * range * 0.55, y, z + Math.cos(yaw) * range * 0.55,
    FLAT, yawOf(yaw + Math.PI / 2), 0, {
    normal: true, c: GC.hot, s: range * 2.2, s1: range * 2.6, ar: 0.13,
    t: 0.09, op: 1, pow: 0.6
  });
  gband(x + Math.sin(yaw) * range * 0.4, z + Math.cos(yaw) * range * 0.4,
    0.14, range * 0.9, range * 1.8, 0.22, GC.acc, 0.6);
  for (var i = 0; i < 4; i++) {
    var a = yaw + (i - 1.5) * 0.44;
    shard(x + Math.sin(a) * range * 0.7, y, z + Math.cos(a) * range * 0.7, a, 0.3, GC.chip, 13, 0.7);
  }
}

/** 地面衝擊環。第三版把它導向「揚塵」（柔邊、亮、會飄），
    硬邊語言裡改成一圈躺平的多邊形厚帶。

    **`r0 > r1` 就是收縮**，那是「標定」的語意（軌道支配的鎖定框用它）；
    `r0 < r1` 是擴散，那是「衝擊」。同一支函式兩種讀法，呼叫端不用改。 */
export function shockRing(x: number, z: number, _c: number, r0: number, r1: number, t: number, op?: number) {
  gband(x, z, 0.14, Math.max(0.5, r0), r1, t + 0.15, GC.acc, op === undefined ? 0.55 : op * 0.85);
}

/** 飛彈排氣。第三版是一串會擴散的白煙團（柔邊）——
    在硬邊語言裡改成**快速縮小的暗色多邊形**：它一樣會遮住背後、一樣
    標出飛行路徑，但它有輪廓，而且**是暗的**（排氣是煙不是光）。 */
export function exhaust(x: number, y: number, z: number, scale: number) {
  if (!take(1)) return;
  spawnQuad(texPolyFill(6), x, y, z, FLAT, rnd(0, 6.283), 0, {
    normal: true, c: GC.chip, s: 1.1 * scale, s1: 0.2 * scale,
    t: rnd(0.30, 0.5), op: 0.75, pow: 1.0, ease: 1.2, spin: rnd(-4, 4)
  });
}
